import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useAdvertiserSearch } from './hooks';

const AvailableAdvertisersSection = ({ user_role, currentUserAdvertisers, invitedUserExistingAccess, onAdvertiserAdded }) => {
  const [addPlatform, setAddPlatform] = useState('DSP');
  // State for multi-selection
  const [selectedAdvertisers, setSelectedAdvertisers] = useState([]);

  // Get available advertisers based on user role
  const getAvailableAdvertisers = () => {
    if (!currentUserAdvertisers) return [];

    if (user_role === 'admin') {
      const adminAdvertisers = [...(currentUserAdvertisers.advertisers_utc || []), ...(currentUserAdvertisers.advertisers_non_utc || [])];
      return adminAdvertisers;
    } else if (user_role === 'internal') {
      const internalAdvertisers = [
        ...(currentUserAdvertisers.advertisers_utc || []),
        ...(currentUserAdvertisers.advertisers_non_utc || []),
        ...(currentUserAdvertisers.advertisers_ctv || []),
        ...(currentUserAdvertisers.advertisers_gtv || [])
      ];
      const seen = new Set();
      return internalAdvertisers.filter((a) => {
        const k = a.id || a.name;
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    }

    return [];
  };

  const availableAdvertisers = getAvailableAdvertisers();

  // Filter out advertisers already in Current Access for the selected platform bucket
  const filteredAdvertisers = availableAdvertisers.filter((advertiser) => {
    if (!invitedUserExistingAccess) return true;

    const inUtc = invitedUserExistingAccess.advertisers_utc?.some((adv) => adv.id === advertiser.id);
    const inJst = invitedUserExistingAccess.advertisers_non_utc?.some((adv) => adv.id === advertiser.id);
    const inCtv = invitedUserExistingAccess.advertisers_ctv?.some((adv) => adv.id === advertiser.id);
    const inGtv = invitedUserExistingAccess.advertisers_gtv?.some((adv) => adv.id === advertiser.id);

    if (addPlatform === 'CTV') {
      return !inCtv;
    }
    if (addPlatform === 'GTV') {
      return !inGtv;
    }
    return !inUtc && !inJst;
  });

  // Search functionality
  const {
    searchTerm,
    filteredData: searchResults,
    updateSearchTerm
  } = useAdvertiserSearch(filteredAdvertisers, ['name', 'sfId', 'org_name', 'timezone']);

  // Handle adding advertiser (platform: DSP | CTV; CTV always uses UTC bucket in state)
  const handleAddAdvertiser = (advertiser, timezone, platform = 'DSP') => {
    if (onAdvertiserAdded) {
      onAdvertiserAdded(advertiser, timezone, platform);
    }
  };

  // Handle multi-selection
  const handleSelectAdvertiser = (advertiser, isSelected) => {
    if (isSelected) {
      setSelectedAdvertisers((prev) => [...prev, advertiser]);
    } else {
      setSelectedAdvertisers((prev) => prev.filter((adv) => adv.id !== advertiser.id));
    }
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedAdvertisers(searchResults);
    } else {
      setSelectedAdvertisers([]);
    }
  };

  const handleBulkAddToUTC = () => {
    selectedAdvertisers.forEach((advertiser) => {
      handleAddAdvertiser(advertiser, 'UTC', addPlatform);
    });
    setSelectedAdvertisers([]);
  };

  const handleBulkAddToJST = () => {
    if (addPlatform !== 'DSP') return;
    selectedAdvertisers.forEach((advertiser) => {
      handleAddAdvertiser(advertiser, 'JST', 'DSP');
    });
    setSelectedAdvertisers([]);
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        mb: 3
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
          Available Advertisers - Select platform, then timezone when adding
        </Typography>
        {selectedAdvertisers.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="contained" color="primary" onClick={handleBulkAddToUTC} sx={{ fontSize: '11px' }}>
              {addPlatform === 'CTV'
                ? `Add ${selectedAdvertisers.length} to CTV (UTC)`
                : addPlatform === 'GTV'
                ? `Add ${selectedAdvertisers.length} to GTV (UTC)`
                : `Add ${selectedAdvertisers.length} to DSP UTC`}
            </Button>
            {addPlatform === 'DSP' && (
              <Button size="small" variant="contained" color="secondary" onClick={handleBulkAddToJST} sx={{ fontSize: '11px' }}>
                Add {selectedAdvertisers.length} to DSP JST
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={() => setSelectedAdvertisers([])} sx={{ fontSize: '11px' }}>
              Clear Selection
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 2, maxWidth: 280 }}>
        <FormControl size="small" fullWidth>
          <InputLabel id="available-add-platform-label">Platform</InputLabel>
          <Select
            labelId="available-add-platform-label"
            label="Platform"
            value={addPlatform}
            onChange={(e) => {
              setAddPlatform(e.target.value);
              setSelectedAdvertisers([]);
            }}
          >
            <MenuItem value="DSP">DSP</MenuItem>
            <MenuItem value="CTV">CTV</MenuItem>
            <MenuItem value="GTV">GTV</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search anything - name, SF ID, organization, timezone, etc..."
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(e) => updateSearchTerm(e.target.value)}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: 'white',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
              borderWidth: 2
            }
          }
        }}
        InputProps={{
          startAdornment: (
            <Box
              sx={{
                color: 'primary.main',
                mr: 1,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              🔍
            </Box>
          )
        }}
      />

      {/* Advertisers Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  color: 'white'
                }}
              >
                <Checkbox
                  checked={selectedAdvertisers.length > 0 && selectedAdvertisers.length === searchResults.length}
                  indeterminate={selectedAdvertisers.length > 0 && selectedAdvertisers.length < searchResults.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                />
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  color: 'white'
                }}
              >
                Advertiser Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  color: 'white'
                }}
              >
                SF ID
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  color: 'white'
                }}
              >
                Organization
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  color: 'white'
                }}
              >
                Timezone
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  color: 'white'
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searchResults.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    {searchTerm ? 'No advertisers matching search criteria' : 'No available advertisers to add'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              searchResults.map((advertiser, index) => (
                <TableRow key={`${advertiser.id || advertiser.name}-${index}`} hover>
                  <TableCell>
                    <Checkbox
                      checked={selectedAdvertisers.some((selected) => selected.id === advertiser.id)}
                      onChange={(e) => handleSelectAdvertiser(advertiser, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{advertiser.name}</TableCell>
                  <TableCell>{advertiser.sfId || 'N/A'}</TableCell>
                  <TableCell>{advertiser.org_name || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={advertiser.timezone || 'Any'}
                      size="small"
                      color={advertiser.timezone === 'UTC' ? 'primary' : advertiser.timezone === 'JST' ? 'secondary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAddAdvertiser(advertiser, 'UTC', addPlatform)}
                        sx={{ fontSize: '11px', py: 0.5 }}
                      >
                        {addPlatform === 'CTV' ? 'Add to CTV (UTC)' : addPlatform === 'GTV' ? 'Add to GTV (UTC)' : 'Add to DSP UTC'}
                      </Button>
                      {addPlatform === 'DSP' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleAddAdvertiser(advertiser, 'JST', 'DSP')}
                          sx={{ fontSize: '11px', py: 0.5 }}
                        >
                          Add to DSP JST
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Results Count */}
      {searchResults.length > 0 && (
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Typography variant="caption" color="textSecondary">
            Showing {searchResults.length} of {filteredAdvertisers.length} available advertisers
          </Typography>
        </Box>
      )}
    </Box>
  );
};

AvailableAdvertisersSection.propTypes = {
  user_role: PropTypes.string,
  currentUserAdvertisers: PropTypes.object,
  invitedUserExistingAccess: PropTypes.object,
  onAdvertiserAdded: PropTypes.func
};

AvailableAdvertisersSection.defaultProps = {
  user_role: 'admin',
  currentUserAdvertisers: {},
  invitedUserExistingAccess: { advertisers_utc: [], advertisers_non_utc: [], advertisers_ctv: [], advertisers_gtv: [] },
  onAdvertiserAdded: () => {}
};

export default AvailableAdvertisersSection;
